<script setup lang="ts">
// The district's weather: what it is doing now, the week ahead, the heat the
// field is working in, and how long a generation takes at the current rate —
// with the egg → adult stages drawn to scale beside that figure.
//
// The payload is one set of units — Fahrenheit, km/h and millimetres, which is
// what NWS gives — and the card reads either that or metric: a per-browser
// choice, seeded from the district's own unit preference. The wire never
// changes and the toggle never refetches. Degree days are Celsius in both
// modes: they are a species requirement, not a temperature reading.
import { computed, ref } from "vue";
import LocalizedNumber from "@/components/LocalizedNumber.vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import DevelopmentTimeline from "@/components/home/DevelopmentTimeline.vue";
import { formatAmount } from "@/components/map-review/charts/theme";
import { loadHomeMetric, saveHomeMetric } from "@/lib/homePrefs";
import { useSessionStore } from "@/store/session";
import type { HomeWeatherCard } from "@/type/api";

const props = defineProps<{
	weather: HomeWeatherCard;
}>();

const session = useSessionStore();

const eggToAdult = computed(() => props.weather.egg_to_adult);

/** OSHA heat priority day: 80 °F heat index (CPL 03-00-024). */
const HEAT_PRIORITY_F = 80;
/** Cal/OSHA high-heat procedures: 95 °F heat index (Title 8 §3395). */
const HEAT_HIGH_F = 95;

/** km/h to mph and millimetres to inches, for display only — the wire stays
 *  km/h and millimetres. */
const KPH_TO_MPH = 0.621371;
const MM_TO_INCH = 0.0393701;

/** °F to °C, for the opposite reading beside a temperature. */
function fahrenheitToCelsius(fahrenheit: number): number {
	return ((fahrenheit - 32) * 5) / 9;
}

/** This browser's stored choice, or null when it has never chosen. */
const storedMetric = ref<boolean | null>(loadHomeMetric());

/**
 * Whether the card reads metric. A stored choice wins, in both directions;
 * with none, the district's own preset seeds it — "metric" for metric,
 * anything else (an absent organisation included) for US customary.
 * `UnitPreference` carries no temperature dimension, so the preset is a
 * sensible seed here rather than a literal temperature setting.
 */
const metric = computed(
	() =>
		storedMetric.value ??
		session.organization?.unit_preference?.preset === "metric",
);

/** The toggle writes the choice through, so it outlives the visit. */
function setMetric(value: boolean): void {
	storedMetric.value = value;
	saveHomeMetric(value);
}

/**
 * The forecast day carrying the highest heat index. OSHA's heat priority day
 * starts at 80 °F heat index; Cal/OSHA requires high-heat procedures at 95 °F.
 * The metric is the heat index, not the dry-bulb temperature — and the
 * payload is Fahrenheit, so the thresholds are read against it directly.
 */
const peakHeatDay = computed(() =>
	props.weather.forecast.reduce((peak, day) =>
		day.heat_index_f > peak.heat_index_f ? day : peak,
	),
);

const heatTriggered = computed(
	() => peakHeatDay.value.heat_index_f >= HEAT_PRIORITY_F,
);
const heatDanger = computed(
	() => peakHeatDay.value.heat_index_f >= HEAT_HIGH_F,
);

const heatNote = computed(() =>
	heatDanger.value
		? `Danger band — high-heat procedures at ≥ ${HEAT_HIGH_F} °F heat index (Cal/OSHA Title 8 §3395).`
		: `Warning band — heat priority day at ≥ ${HEAT_PRIORITY_F} °F heat index (OSHA CPL 03-00-024); shade required.`,
);

/**
 * The current temperature, as the card reads it: the chosen unit large, the
 * same temperature in the other unit small and muted beside it. Fahrenheit is
 * whole degrees, Celsius one decimal.
 */
const currentTemperature = computed(() => {
	const fahrenheit = props.weather.current.temperature_f;
	const celsius = fahrenheitToCelsius(fahrenheit);
	return metric.value
		? {
				primary: `${celsius.toFixed(1)}°`,
				secondary: `${Math.round(fahrenheit)} °F`,
			}
		: {
				primary: `${Math.round(fahrenheit)}°`,
				secondary: `${celsius.toFixed(1)} °C`,
			};
});

/** Wind speed as the card reads it: the wire's km/h, or US customary mph. */
const windReading = computed(() =>
	metric.value
		? `${props.weather.current.wind_kph} km/h`
		: `${(props.weather.current.wind_kph * KPH_TO_MPH).toFixed(1)} mph`,
);

/** Today's precipitation as the card reads it: millimetres, or inches. */
const precipitationReading = computed(() =>
	metric.value
		? `${props.weather.current.precipitation_mm} mm`
		: `${(props.weather.current.precipitation_mm * MM_TO_INCH).toFixed(2)} in`,
);

/**
 * The forecast strip in the unit the card is reading: the day's high, its low,
 * and the heat chip's reading with its suffix. Each day keeps the payload's
 * `heat_index_f` as well, because the chips are gated and coloured on that.
 */
const forecastDays = computed(() =>
	props.weather.forecast.map((day) => ({
		...day,
		high: metric.value
			? Math.round(fahrenheitToCelsius(day.high_f))
			: Math.round(day.high_f),
		low: metric.value
			? Math.round(fahrenheitToCelsius(day.low_f))
			: Math.round(day.low_f),
		heat_value: metric.value
			? Math.round(fahrenheitToCelsius(day.heat_index_f))
			: day.heat_index_f,
		heat_unit: metric.value ? "°C" : "°F",
	})),
);

/** The heat strip's peak reading, in the unit the card is reading. */
const peakHeatReading = computed(() =>
	metric.value
		? `${Math.round(fahrenheitToCelsius(peakHeatDay.value.heat_index_f))} °C`
		: `${peakHeatDay.value.heat_index_f} °F`,
);

/** The rate the days were derived from, and the method behind it. */
const rateNote = computed(
	() =>
		`rolling 7-day mean ${formatAmount(eggToAdult.value.daily_degree_day_rate)} DD °C/day, base 10 °C, average method, biofix Jan 1`,
);
</script>

<template>
	<div class="card">
		<div
			class="card-header d-flex align-items-center justify-content-between gap-2 py-1"
		>
			<span class="fw-semibold">Weather</span>
			<div class="d-flex align-items-center gap-2">
				<span class="small text-muted">
					observed
					<TimeRelativeWithTooltip :time="weather.current.observed_at" />
				</span>
				<div
					class="btn-group btn-group-sm unit-toggle"
					role="group"
					aria-label="Units"
				>
					<button
						type="button"
						class="btn"
						:class="metric ? 'btn-outline-secondary' : 'btn-secondary'"
						:aria-pressed="!metric"
						title="US customary: °F, mph, in"
						@click="setMetric(false)"
					>
						°F
					</button>
					<button
						type="button"
						class="btn"
						:class="metric ? 'btn-secondary' : 'btn-outline-secondary'"
						:aria-pressed="metric"
						title="Metric: °C, km/h, mm"
						@click="setMetric(true)"
					>
						°C
					</button>
				</div>
			</div>
		</div>
		<div class="card-body py-2">
			<div class="row g-2">
				<div class="col-xl-3">
					<div class="d-flex align-items-baseline gap-2">
						<span class="fs-2 fw-bold">{{ currentTemperature.primary }}</span>
						<span class="small text-muted">
							{{ currentTemperature.secondary }}
						</span>
						<span class="text-muted">{{ weather.current.condition }}</span>
					</div>
					<div class="small text-muted">
						Wind {{ windReading }} {{ weather.current.wind_direction }} ·
						Precipitation {{ precipitationReading }}
					</div>
				</div>
				<div class="col-xl-9">
					<div class="row g-1 text-center">
						<div
							v-for="day in forecastDays"
							:key="day.day"
							class="col-6 col-sm-4 col-lg"
						>
							<div class="border rounded py-1 h-100">
								<div class="fw-semibold small">{{ day.day }}</div>
								<i
									class="bi d-block text-secondary"
									:class="day.icon"
									:title="day.condition"
									aria-hidden="true"
								></i>
								<div class="small">
									<LocalizedNumber :value="day.high" />° /
									<LocalizedNumber :value="day.low" />°
								</div>
								<div class="small text-muted">
									<LocalizedNumber :value="day.precipitation_chance" />%
								</div>
								<div
									v-if="day.heat_index_f >= HEAT_PRIORITY_F"
									class="badge rounded-pill"
									:class="
										day.heat_index_f >= HEAT_HIGH_F
											? 'text-bg-danger'
											: 'text-bg-warning'
									"
									:title="`heat index ${day.heat_value} ${day.heat_unit}`"
								>
									{{ day.heat_value }}{{ day.heat_unit }}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div
				v-if="heatTriggered"
				class="heat-strip d-flex align-items-center gap-2 small rounded px-2 py-1 mt-2"
				:class="heatDanger ? 'heat-strip--danger' : 'heat-strip--warning'"
			>
				<i class="bi bi-thermometer-high" aria-hidden="true"></i>
				<span class="fw-semibold">
					{{ peakHeatDay.day }}: peak heat index {{ peakHeatReading }}
				</span>
				<span>{{ heatNote }}</span>
			</div>

			<div
				class="row g-2 align-items-center mt-2 pt-2 border-top border-light-subtle"
			>
				<div class="col-xl-3">
					<span class="fs-2 fw-bold">
						{{ formatAmount(eggToAdult.average_days) }}
					</span>
					<span class="small text-muted" :title="rateNote">
						days egg → adult
					</span>
				</div>
				<div class="col-xl-9">
					<DevelopmentTimeline :egg-to-adult="eggToAdult" />
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.heat-strip--warning {
	background: #fff3cd;
	border: 1px solid #ffe69c;
	color: #664d03;
}

.heat-strip--danger {
	background: #f8d7da;
	border: 1px solid #f5c2c7;
	color: #58151c;
}

/* The units toggle rides in the card header and must not make it taller — the
 * card sits above everything else on a one-screen page, so the buttons stay
 * shorter than the header's own text line. */
.unit-toggle .btn {
	font-size: 0.72rem;
	padding: 0.1rem 0.45rem;
	line-height: 1.2;
}
</style>
